import { useEffect, useState, useCallback } from "react";
import { GeoCell } from "@/components/GeoCell";
import axios from "axios";
import { toast } from "sonner";
import { API, getAuthHeader } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RefreshCw, LogOut, AlertTriangle } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString([], { dateStyle: "short", timeStyle: "short" }) : "—");
const dur = (a, b) => {
  const m = Math.max(0, Math.round((new Date(b || Date.now()) - new Date(a)) / 60000));
  return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`;
};

export const LiveUsersPanel = () => {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/security/sessions`, { headers: getAuthHeader(), params: { date } });
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load sessions"); }
  }, [date]);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const forceLogout = async (s) => {
    if (!window.confirm(`Force logout ${s.name || s.email} from every device?`)) return;
    try {
      await axios.post(`${API}/admin/users/${s.user_id}/force-logout`, {}, { headers: getAuthHeader() });
      toast.success("User logged out everywhere"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-3" data-testid="live-users-panel">
      <div className="flex items-center gap-2">
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-8 w-40" data-testid="live-users-date" />
        <Button size="sm" variant="outline" onClick={load} data-testid="live-users-refresh"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh</Button>
        <span className="text-sm text-slate-600 ml-2" data-testid="live-users-count">
          <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 animate-pulse mr-1.5" />
          {data?.live_count ?? 0} live now · {data?.sessions.length ?? 0} session(s) this day
        </span>
      </div>
      <div className="overflow-auto border rounded-lg">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-slate-600">
            <tr>{["User", "Team", "Status", "Started", "Closed / last seen", "Duration", "IP address", "Location", ""].map(h => <th key={h} className="p-2 text-left">{h}</th>)}</tr>
          </thead>
          <tbody>
            {(data?.sessions || []).map((s, i) => (
              <tr key={s.sid} className="border-t" data-testid={`live-session-${i}`}>
                <td className="p-2 font-medium">{s.name || "—"}<div className="text-[10px] text-slate-400">{s.email}</div></td>
                <td className="p-2">{s.team}</td>
                <td className="p-2">
                  {s.live
                    ? <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-semibold" data-testid={`live-session-status-${i}`}>LIVE</span>
                    : <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-500" data-testid={`live-session-status-${i}`}>{s.end_reason || "closed"}</span>}
                </td>
                <td className="p-2 whitespace-nowrap">{fmt(s.started_at)}</td>
                <td className="p-2 whitespace-nowrap">{s.live ? `active · ${fmt(s.last_seen)}` : fmt(s.closed_at)}</td>
                <td className="p-2">{dur(s.started_at, s.live ? null : s.closed_at)}</td>
                <td className="p-2 font-mono">
                  {s.ip}
                  {s.new_ip && <span className="ml-1 px-1 rounded bg-amber-100 text-amber-800 text-[10px] font-sans font-semibold" title="New IP — verify in Security tab"><AlertTriangle className="w-3 h-3 inline" /> new IP</span>}
                  {s.last_ip && s.last_ip !== s.ip && <div className="text-[10px] text-slate-400">now {s.last_ip}</div>}
                </td>
                <td className="p-2">
                  <GeoCell geo={s.geo} ip={s.ip} testId={`live-session-geo-${i}`} />
                  {s.last_ip && s.last_ip !== s.ip && <div><GeoCell geo={s.last_geo} ip={s.last_ip} compact testId={`live-session-last-geo-${i}`} /></div>}
                </td>
                <td className="p-2">
                  {s.live && (
                    <Button size="sm" variant="ghost" className="h-6 px-2 text-red-600" onClick={() => forceLogout(s)} data-testid={`live-force-logout-${i}`}>
                      <LogOut className="w-3 h-3 mr-1" /> Logout
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {data && !data.sessions.length && <tr><td colSpan={9} className="p-6 text-center text-slate-400">No sessions on this day</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};
